import { Link } from "react-router-dom";
import styled from "styled-components";

export const Card = styled(Link)<{
  $backgroundColor: string;
  $textColor: string;
}>`
  position: relative;
  isolation: isolate;
  display: block;
  width: 100%;
  aspect-ratio: 1.58 / 1;
  min-height: 286px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  box-shadow: 0 4px 8px 0 rgba(191, 191, 191, 0.15);
  color: ${({ $textColor }) => $textColor};
  text-decoration: none;
  cursor: pointer;
  transition: transform 280ms ease, box-shadow 280ms ease;

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    transition: opacity 360ms ease;
  }

  &::before {
    z-index: -1;
    background: linear-gradient(
      180deg,
      transparent 2%,
      ${({ $backgroundColor }) => $backgroundColor} 100%
    );
    opacity: 0.96;
  }

  &::after {
    z-index: 0;
    background-color: ${({ theme }) => theme.colors.greyBlue};
    opacity: 0;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 4px;
  }

  @media (hover: hover) {
    &:hover::before {
      opacity: 0;
    }

    &:hover::after {
      opacity: 1;
    }
  }

  @media (max-width: 1024px) {
    aspect-ratio: 2.05 / 1;
    min-height: 237px;
  }

  @media (max-width: 520px) {
    aspect-ratio: 1.58 / 1;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &::before,
    &::after {
      transition-duration: 0.01ms;
    }
  }

  @media (min-width: 3400px) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
`;

export const CardImage = styled.img<{
  $imageFit: "cover" | "contain";
  $backgroundColor: string;
}>`
  position: absolute;
  z-index: -2;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: ${({ $imageFit }) => $imageFit};
  object-position: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  transition: opacity 360ms ease, transform 520ms ease;

  ${Card}:hover & {
    @media (hover: hover) {
      opacity: 0;
      transform: scale(1.04);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }
`;

export const ArrowButton = styled.span<{
  $buttonColor: string;
  $arrowColor: string;
}>`
  position: absolute;
  z-index: 3;
  top: ${({ theme }) => theme.padding.regular};
  right: ${({ theme }) => theme.padding.regular};
  display: flex;
  min-width: 42px;
  height: 42px;
  padding: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 999px;
  background-color: ${({ $buttonColor }) => $buttonColor};
  color: ${({ $arrowColor }) => $arrowColor};
  box-sizing: border-box;
  transition: min-width 320ms ease, padding 320ms ease;

  span {
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    white-space: nowrap;
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    transition: max-width 320ms ease, opacity 220ms ease;
    align-self: center;
  }

  i {
    font-size: 1.25rem;
  }

  ${Card}:hover & {
    @media (hover: hover) {
      min-width: 126px;
      padding: 0 14px;
      background-color: ${({ theme }) => theme.colors.offWhite};
      color: ${({ theme }) => theme.colors.darkBlue};

      span {
        max-width: 152px;
        opacity: 1;
      }
    }
  }

  @media (max-width: 520px) {
    min-width: 36px;
    height: 36px;

    i {
      font-size: 1.15rem;
    }
  }
  @media (min-width: 2500px) {
    min-width: 60px;
    height: 60px;

    span {
      font-size: 1.525rem;
    }

    i {
      font-size: 1.75rem;
    }

    ${Card}:hover & {
      @media (hover: hover) {
        min-width: 150px;
        padding: 0 25px;
        height: 60px;

        span {
          max-width: 200px;
        }
      }
    }
  }

  @media (min-width: 3000px) {
    top: ${({ theme }) => theme.padding.medium};
    right: ${({ theme }) => theme.padding.medium};
    min-width: 72px;
    height: 72px;

    span {
      font-size: 1.75rem;
    }

    i {
      font-size: 2.25rem;
    }

    ${Card}:hover & {
      @media (hover: hover) {
        min-width: 150px;
        padding: 0 40px;
        height: 72px;

        span {
          max-width: 300px;
        }
      }
    }
  }
  @media (min-width: 3400px) {
    top: ${({ theme }) => theme.padding.large};
    right: ${({ theme }) => theme.padding.large};
    min-width: 82px;
    height: 82px;
  }
  @media (min-width: 3800px) {
    span {
      font-size: 2rem;
    }
    i {
      font-size: 2.5rem;
    }

    min-width: 82px;
    height: 82px;

    ${Card}:hover & {
      @media (hover: hover) {
        min-width: 180px;
        padding: 0 40px;
        height: 82px;

        span {
          max-width: 320px;
        }
      }
    }
  }
  @media (min-width: 4500px) {
    span {
      font-size: 2.5rem;
    }
    i {
      font-size: 3rem;
    }

    min-width: 92px;
    height: 92px;

    ${Card}:hover & {
      @media (hover: hover) {
        min-width: 180px;
        padding: 0 40px;
        height: 92px;

        span {
          max-width: 360px;
        }
      }
    }
  }
  @media (min-width: 5000px) {
    span {
      font-size: 3rem;
    }
    i {
      font-size: 3.5rem;
    }

    min-width: 102px;
    height: 102px;

    ${Card}:hover & {
      @media (hover: hover) {
        min-width: 180px;
        padding: 0 40px;
        height: 102px;

        span {
          max-width: 410px;
        }
      }
    }
  }
`;

export const DefaultContent = styled.div`
  position: absolute;
  z-index: 2;
  right: ${({ theme }) => theme.padding.regular};
  bottom: ${({ theme }) => theme.padding.regular};
  left: ${({ theme }) => theme.padding.regular};
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: opacity 260ms ease, transform 360ms ease;

  h3,
  p {
    margin: 0;
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.body.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.extrabold};
    line-height: 1.2;
    text-transform: uppercase;
    white-space: pre-line;
  }

  p {
    font-size: ${({ theme }) => theme.typography.body.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    line-height: 1.35;
    white-space: pre-line;
  }

  ${Card}:hover & {
    @media (hover: hover) {
      opacity: 0;
      transform: translateY(18px);
    }
  }

  @media (max-width: 520px) {
    h3,
    p {
      font-size: ${({ theme }) => theme.typography.medium.fontSize};
    }
  }

  @media (min-width: 2500px) {
    h3,
    p {
      font-size: 1.75rem;
    }
  }
  @media (min-width: 3000px) {
    right: ${({ theme }) => theme.padding.medium};
    bottom: ${({ theme }) => theme.padding.medium};
    left: ${({ theme }) => theme.padding.medium};

    h3,
    p {
      font-size: 2rem;
    }
  }
  @media (min-width: 3400px) {
    right: ${({ theme }) => theme.padding.large};
    bottom: ${({ theme }) => theme.padding.large};
    left: ${({ theme }) => theme.padding.large};

    h3,
    p {
      font-size: 2.25rem;
    }
  }
  @media (min-width: 3800px) {
    h3,
    p {
      font-size: 2.5rem;
    }
  }
  @media (min-width: 4500px) {
    h3,
    p {
      font-size: 3rem;
    }
  }
  @media (min-width: 5000px) {
    h3,
    p {
      font-size: 3.5rem;
    }
  }
`;

export const HoverContent = styled.div`
  position: absolute;
  z-index: 2;
  top: 64px;
  right: ${({ theme }) => theme.padding.regular};
  bottom: ${({ theme }) => theme.padding.regular};
  left: ${({ theme }) => theme.padding.regular};
  display: flex;
  align-items: flex-end;
  opacity: 0;
  transform: translateY(18px);
  color: ${({ theme }) => theme.colors.offWhite};
  transition: opacity 300ms ease 50ms, transform 360ms ease 50ms;

  p {
    display: -webkit-box;
    margin: 0;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 6;
    font-size: ${({ theme }) => theme.typography.body.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    line-height: 1.45;
  }

  ${Card}:hover & {
    @media (hover: hover) {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 520px) {
    p {
      font-size: ${({ theme }) => theme.typography.medium.fontSize};
      line-height: normal;
      -webkit-line-clamp: 7;
    }
  }

  @media (min-width: 2500px) {
    p {
      font-size: 1.75rem;
    }
  }
  @media (min-width: 3000px) {
    right: ${({ theme }) => theme.padding.medium};
    bottom: ${({ theme }) => theme.padding.medium};
    left: ${({ theme }) => theme.padding.medium};

    p {
      font-size: 2rem;
    }
  }
  @media (min-width: 3400px) {
    right: ${({ theme }) => theme.padding.large};
    bottom: ${({ theme }) => theme.padding.large};
    left: ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3800px) {
    p {
      font-size: 2.5rem;
    }
  }
  @media (min-width: 4500px) {
    p {
      font-size: 3rem;
    }
  }
  @media (min-width: 5000px) {
    p {
      font-size: 3.5rem;
    }
  }
`;
